/* CA EVIDENCE START — schema 1.0
 * Central storefront evidence publisher for CherryAffairs.
 * GA4 standard ecommerce remains owned by Shopify's Google & YouTube channel.
 */
(function () {
  'use strict';

  var previous = window.CAEvidence || {};
  var pending = Array.isArray(previous.q) ? previous.q.slice() : [];
  var SCHEMA_VERSION = '1.0';
  var EXPERIENCE_ID = 'enterprise_main_filter_2026_08';
  var EXPERIENCE_VARIANT = 'enterprise_facets_v3';
  var FILTER_UI_VERSION = 'enterprise_facets_v3';
  var FILTER_UPDATE_TIMEOUT_MS = 10000;
  var FILTER_GROUP_TOGGLE_INTENT_TTL_MS = 2000;
  var WEB_VITAL_SAMPLE_RATE = 0.1;
  var WEB_VITAL_SAMPLE_KEY = 'ca_web_vital_sample_v1';
  var STORAGE_KEY = 'ca_evidence_lineage_v1';
  var STATE_TTL_MS = 45 * 60 * 1000;
  var FILTER_DEBOUNCE_MS = 400;
  var MAX_ARRAY_LENGTH = 50;
  var MAX_STRING_LENGTH = 200;
  var recentlyPublished = new Map();
  var recommendationImpressions = new Set();
  var debounceTimers = new Map();
  var rebuyWidgetStates = new Map();
  var rebuyImpressions = new Set();
  var rebuyRecentSelections = new Map();
  var rebuyInferredIntentId = null;
  var rebuyScanTimer = null;
  var pendingFacetInteractions = [];
  var pendingFacetTimer = null;
  var lastFacetSnapshot = null;
  var pendingFacetGroupToggles = new WeakMap();
  var guideCtaObserver = null;
  var guideProgressMarks = new Set();

  function now() {
    return Date.now();
  }

  function uuid() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }

    return 'ca-' + now().toString(36) + '-' + Math.random().toString(36).slice(2, 12);
  }

  function isBlockedKey(key) {
    if (/^(query|search)_length$/i.test(key)) return false;
    return /^(email|phone|telephone|address|customer|customer_id|user_id|message|notes?|raw_text|audience|sensation|experience|control|priority|goal|need|filter_value|value)$/i.test(key) ||
      /(search|query|keyword|answer|free_?text)/i.test(key);
  }

  function sanitize(value, depth) {
    depth = depth || 0;
    if (depth > 4 || value == null) return value == null ? null : undefined;

    if (typeof value === 'string') return value.slice(0, MAX_STRING_LENGTH);
    if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
    if (typeof value === 'boolean') return value;

    if (Array.isArray(value)) {
      return value.slice(0, MAX_ARRAY_LENGTH).map(function (item) {
        return sanitize(item, depth + 1);
      }).filter(function (item) {
        return item !== undefined;
      });
    }

    if (typeof value === 'object') {
      var clean = {};
      Object.keys(value).slice(0, 50).forEach(function (key) {
        if (isBlockedKey(key)) return;
        var item = sanitize(value[key], depth + 1);
        if (item !== undefined) clean[key] = item;
      });
      return clean;
    }

    return undefined;
  }

  function readStateFrom(storage) {
    try {
      var raw = storage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var state = JSON.parse(raw);
      if (!state || !state.expires_at || state.expires_at <= now()) {
        storage.removeItem(STORAGE_KEY);
        return null;
      }
      return state;
    } catch (error) {
      return null;
    }
  }

  function getState() {
    return readStateFrom(window.localStorage) || readStateFrom(window.sessionStorage) || {};
  }

  function saveState(next) {
    var clean = {
      intent_id: next.intent_id || null,
      recommendation_id: next.recommendation_id || null,
      recommendation_version: next.recommendation_version || null,
      selected_item_id: next.selected_item_id || null,
      selected_variant_id: next.selected_variant_id || null,
      selected_rank: next.selected_rank != null ? next.selected_rank : null,
      selected_at: next.selected_at || null,
      expires_at: now() + STATE_TTL_MS
    };

    var serialized = JSON.stringify(clean);
    try { window.localStorage.setItem(STORAGE_KEY, serialized); } catch (error) { /* storage unavailable */ }
    try { window.sessionStorage.setItem(STORAGE_KEY, serialized); } catch (error) { /* storage unavailable */ }
    return clean;
  }

  function clearExpiredPublicationKeys() {
    var cutoff = now() - 10000;
    recentlyPublished.forEach(function (timestamp, key) {
      if (timestamp < cutoff) recentlyPublished.delete(key);
    });
  }

  function isDuplicate(name, payload, windowMs) {
    clearExpiredPublicationKeys();
    var key = name + '|' + JSON.stringify(payload);
    var last = recentlyPublished.get(key) || 0;
    if (now() - last < (windowMs || 1200)) return true;
    recentlyPublished.set(key, now());
    return false;
  }

  function markRuntime(attribute, value) {
    try { document.documentElement.setAttribute(attribute, value); } catch (error) { /* DOM unavailable */ }
  }

  function publishToShopify(name, payload, attempt) {
    attempt = attempt || 0;
    if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
      try {
        var result = Shopify.analytics.publish('ca:' + name, payload);
        if (result && typeof result.then === 'function') {
          result.then(function (dispatched) {
            if (dispatched !== false) markRuntime('data-ca-evidence-delivered', 'ca:' + name);
          }).catch(function () {
            markRuntime('data-ca-evidence-delivery', 'failed');
          });
        } else {
          markRuntime('data-ca-evidence-delivered', 'ca:' + name);
        }
        return;
      } catch (error) {
        if (attempt >= 3) return;
      }
    }

    if (attempt < 3) {
      window.setTimeout(function () {
        publishToShopify(name, payload, attempt + 1);
      }, 250 * (attempt + 1));
    }
  }

  function publish(name, payload, options) {
    options = options || {};
    var state = getState();
    var envelope = sanitize(Object.assign({
      schema_version: SCHEMA_VERSION,
      experience_id: EXPERIENCE_ID,
      experience_variant: EXPERIENCE_VARIANT,
      event_id: uuid(),
      occurred_at: new Date().toISOString(),
      page_path: window.location.pathname,
      intent_id: payload && payload.intent_id || state.intent_id || null,
      recommendation_id: payload && payload.recommendation_id || state.recommendation_id || null,
      recommendation_version: payload && payload.recommendation_version || state.recommendation_version || null
    }, payload || {}));

    var dedupeCopy = Object.assign({}, envelope);
    delete dedupeCopy.event_id;
    delete dedupeCopy.occurred_at;
    if (!options.allowDuplicate && isDuplicate(name, dedupeCopy, options.dedupeWindowMs)) return false;

    markRuntime('data-ca-evidence-last', 'ca:' + name);
    publishToShopify(name, envelope, 0);
    document.dispatchEvent(new CustomEvent('ca:evidence:published', {
      detail: { name: 'ca:' + name, payload: envelope }
    }));
    return true;
  }

  function captureIntent(payload) {
    payload = payload || {};
    var state = saveState({
      intent_id: payload.intent_id || uuid(),
      recommendation_id: null,
      recommendation_version: payload.recommendation_version || 'theme-1'
    });

    return publish('intent_captured', Object.assign({}, payload, {
      intent_id: state.intent_id,
      recommendation_id: null,
      recommendation_version: state.recommendation_version
    }), { allowDuplicate: true });
  }

  function normalizeItem(source) {
    source = source || {};
    return sanitize({
      item_id: source.item_id || source.product_id || source.product_handle || source.handle || null,
      item_name: source.item_name || source.product_name || source.product_title || source.title || null,
      item_brand: source.item_brand || source.vendor || source.brand || null,
      item_category: source.item_category || source.product_type || source.category || null,
      item_variant: source.item_variant || source.variant_title || null,
      variant_id: source.variant_id || null,
      price: source.price || source.price_shown || null,
      discount: source.discount || source.discount_shown || null,
      index: source.index != null ? source.index : source.position
    });
  }

  function normalizeItems(payload) {
    var candidates = payload.items || payload.products || payload.recommendations || [];
    if (!Array.isArray(candidates)) candidates = [candidates];
    var items = candidates.map(normalizeItem).filter(function (item) {
      return item && (item.item_id || item.item_name);
    });
    if (!items.length) {
      var single = normalizeItem(payload);
      if (single.item_id || single.item_name) items.push(single);
    }
    return items;
  }

  function inferVisibleItems(eventName) {
    eventName = String(eventName || '');
    var selectors = [];

    if (eventName === 'find_your_match') {
      selectors = ['[id^="FindYourMatchQuiz-"] [data-result-grid] [data-product-card]'];
    } else if (/^lelo_/.test(eventName)) {
      selectors = /_recommendation_select$/.test(eventName) ?
        ['[data-rec-grid] .lelo-product'] :
        ['[data-lelo-product]'];
    } else if (/^lovense_/.test(eventName)) {
      selectors = /_recommendation_select$/.test(eventName) ?
        ['[data-route-results] article', '[data-route-results] [class*="product"]'] :
        ['[data-lovense-product]'];
    } else if (eventName === 'satisfyer_route_select') {
      selectors = ['[data-product]'];
    }

    if (!selectors.length || !document.querySelectorAll) return [];

    var nodes = [];
    selectors.forEach(function (selector) {
      Array.prototype.forEach.call(document.querySelectorAll(selector), function (node) {
        if (nodes.indexOf(node) === -1) nodes.push(node);
      });
    });

    var seen = {};
    return nodes.filter(function (node) {
      if (node.hidden) return false;
      if (node.getClientRects && node.getClientRects().length === 0) return false;
      return true;
    }).map(function (node) {
      var link = node.matches && node.matches('a[href*="/products/"]') ?
        node : node.querySelector && node.querySelector('a[href*="/products/"]');
      var href = link && link.getAttribute('href') || '';
      var handle = node.dataset && node.dataset.handle || href.split('/products/')[1] || '';
      handle = handle.split(/[?#]/)[0];
      if (!handle || seen[handle]) return null;
      seen[handle] = true;
      var heading = node.querySelector && node.querySelector('h2, h3, h4');
      return sanitize({
        item_id: handle,
        item_name: heading && heading.textContent && heading.textContent.trim() || null,
        index: Object.keys(seen).length
      });
    }).filter(Boolean).slice(0, MAX_ARRAY_LENGTH);
  }

  function showRecommendations(payload) {
    payload = payload || {};
    var current = getState();
    var intentId = payload.intent_id || current.intent_id || uuid();
    var recommendationId = payload.recommendation_id || uuid();
    var version = payload.recommendation_version || current.recommendation_version || 'theme-1';
    var items = normalizeItems(payload);
    if (!items.length) {
      items = inferVisibleItems(payload.legacy_event || payload.recommendation_source);
    }
    markRuntime('data-ca-evidence-items', String(items.length));
    var signature = [
      payload.recommendation_source || payload.item_list_id || payload.item_list_name || 'unknown',
      intentId,
      items.map(function (item) { return item.item_id || item.item_name; }).join(',')
    ].join('|');

    if (recommendationImpressions.has(signature)) return false;
    recommendationImpressions.add(signature);
    saveState({
      intent_id: intentId,
      recommendation_id: recommendationId,
      recommendation_version: version
    });

    return publish('recommendation_shown', Object.assign({}, payload, {
      intent_id: intentId,
      recommendation_id: recommendationId,
      recommendation_version: version,
      items: items
    }), { allowDuplicate: true });
  }

  function selectRecommendation(payload) {
    payload = payload || {};
    var current = getState();
    var item = normalizeItem(payload.item || payload.product || payload);
    var state = saveState({
      intent_id: payload.intent_id || current.intent_id || null,
      recommendation_id: payload.recommendation_id || current.recommendation_id || null,
      recommendation_version: payload.recommendation_version || current.recommendation_version || null,
      selected_item_id: item.item_id || null,
      selected_variant_id: item.variant_id || null,
      selected_rank: item.index != null ? item.index : null,
      selected_at: new Date().toISOString()
    });

    persistCartLineage(state);

    return publish('recommendation_clicked', Object.assign({}, payload, {
      intent_id: state.intent_id,
      recommendation_id: state.recommendation_id,
      recommendation_version: state.recommendation_version,
      items: [item]
    }));
  }

  function persistCartLineage(state) {
    if (!state || !state.intent_id || !state.recommendation_id || !state.selected_item_id) return;
    if (typeof window.fetch !== 'function') return;

    var attributes = {
      __ca_schema_version: SCHEMA_VERSION,
      __ca_intent_id: String(state.intent_id).slice(0, 200),
      __ca_recommendation_id: String(state.recommendation_id).slice(0, 200),
      __ca_recommendation_version: String(state.recommendation_version || '').slice(0, 100),
      __ca_selected_item_id: String(state.selected_item_id).slice(0, 200),
      __ca_selected_variant_id: String(state.selected_variant_id || '').slice(0, 100),
      __ca_recommendation_rank: state.selected_rank != null ? String(state.selected_rank).slice(0, 20) : '',
      __ca_selected_at: String(state.selected_at || new Date().toISOString()).slice(0, 40)
    };
    var root = window.Shopify && Shopify.routes && Shopify.routes.root || '/';
    var endpoint = root.replace(/\/?$/, '/') + 'cart/update.js';

    window.fetch(endpoint, {
      method: 'POST',
      credentials: 'same-origin',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attributes })
    }).then(function (response) {
      if (!response.ok) throw new Error('Cart lineage update failed');
      markRuntime('data-ca-evidence-cart-lineage', 'persisted');
    }).catch(function () {
      markRuntime('data-ca-evidence-cart-lineage', 'failed');
    });
  }

  function currentProductHandle() {
    var match = String(window.location && window.location.pathname || '').match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  }

  // Classify the route relative to Shopify's actual locale root. The event
  // envelope retains location.pathname, including its locale, for attribution.
  function classifiedPagePath() {
    var path = String(window.location && window.location.pathname || '/');
    var root = String(window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/');
    if (/^\/[a-z]{2,3}(?:-[a-z0-9]{2,8})*\/?$/i.test(root)) {
      var prefix = root.replace(/\/$/, '');
      if (path === prefix) return '/';
      if (path.indexOf(prefix + '/') === 0) return path.slice(prefix.length) || '/';
    }
    return path;
  }

  function rebuyPageSurface() {
    var path = classifiedPagePath();
    if (/^\/cart\/?$/.test(path)) return 'cart';
    if (/^\/blogs\/[^/]+\/[^/]+/.test(path)) return 'article';
    if (/^\/blogs\/[^/]+/.test(path)) return 'blog';
    if (/^\/pages\//.test(path)) {
      if (/(guide|find|help|support|match|discovery)/i.test(path) ||
          document.querySelector && document.querySelector('[data-guide-event]')) return 'guide';
      return 'page';
    }
    if (/^\/(?:collections\/[^/]+\/)?products\//.test(path)) return 'product';
    if (/^\/collections\//.test(path)) return 'collection';
    if (/^\/search/.test(path)) return 'search';
    return path === '/' ? 'home' : 'other';
  }

  function rebuySafeId(value) {
    var safe = String(value == null ? '' : value).replace(/[^a-zA-Z0-9_.-]/g, '').slice(0, 80);
    return safe || 'unknown';
  }

  function rebuyRootFor(widget, widgetId) {
    var element = widget && widget.el && (widget.el.$el || widget.el);
    if (element && element.nodeType === 1) {
      return element.closest && element.closest('[data-rebuy-id]') || element;
    }
    if (!document.querySelectorAll) return null;
    var roots = document.querySelectorAll('[data-rebuy-id]');
    for (var index = 0; index < roots.length; index += 1) {
      if (String(roots[index].getAttribute('data-rebuy-id')) === String(widgetId)) return roots[index];
    }
    return null;
  }

  function rebuyWidgetId(widget, root) {
    var rootId = root && root.getAttribute && root.getAttribute('data-rebuy-id');
    var config = widget && widget.config || {};
    var data = widget && widget.data || {};
    return rebuySafeId(rootId || widget && (widget.id || widget.widget_id) || config.id || data.id);
  }

  function rebuyRootRendered(root) {
    if (!root || root.hidden) return false;
    try {
      if (root.getClientRects && root.getClientRects().length === 0) return false;
      var style = window.getComputedStyle && window.getComputedStyle(root);
      return !style || style.display !== 'none' && style.visibility !== 'hidden';
    } catch (error) {
      return false;
    }
  }

  function rebuyProductArray(detail, widget) {
    if (detail && Array.isArray(detail.products)) return detail.products;
    if (widget && Array.isArray(widget.products)) return widget.products;
    if (widget && widget.data && Array.isArray(widget.data.products)) return widget.data.products;
    return [];
  }

  function rebuyProductItem(product, index) {
    product = product || {};
    var variants = Array.isArray(product.variants) ? product.variants : [];
    var variant = product.selected_variant || product.selectedVariant || variants[0] || {};
    var handle = product.handle || product.product_handle || product.productHandle || '';
    var productId = product.id || product.product_id || product.productId || '';
    return normalizeItem({
      item_id: handle || productId,
      item_name: product.title || product.name || product.product_title || null,
      item_brand: product.vendor || product.brand || null,
      item_category: product.product_type || product.type || null,
      item_variant: variant.title || product.variant_title || null,
      variant_id: variant.id || product.variant_id || product.variantId || null,
      index: product.index != null ? product.index : product.position != null ? product.position : index + 1
    });
  }

  function rebuyDomItems(root) {
    if (!root || !root.querySelectorAll) return [];
    var seen = {};
    return Array.prototype.map.call(root.querySelectorAll('.rebuy-product-block'), function (card, index) {
      var link = card.querySelector('a[href*="/products/"]');
      var href = link && link.getAttribute('href') || '';
      var handle = href.indexOf('/products/') >= 0 ? href.split('/products/')[1].split(/[?#]/)[0] : '';
      var classMatch = String(card.className || '').match(/(?:^|\s)product-id-([0-9]+)/);
      var itemId = handle || classMatch && classMatch[1] || '';
      if (!itemId || seen[itemId]) return null;
      seen[itemId] = true;
      var heading = card.querySelector('.rebuy-product-title, h2, h3, h4, h5');
      return normalizeItem({
        item_id: itemId,
        item_name: heading && heading.textContent && heading.textContent.trim() || null,
        index: index + 1
      });
    }).filter(Boolean).slice(0, MAX_ARRAY_LENGTH);
  }

  function rebuyItems(detail, widget, root) {
    var products = rebuyProductArray(detail, widget);
    var items = products.map(rebuyProductItem).filter(function (item) {
      return item && (item.item_id || item.item_name);
    });
    return items.length ? normalizeItems({ items: items }) : rebuyDomItems(root);
  }

  function rebuyListName(root, widgetId) {
    var heading = root && root.querySelector && root.querySelector('.primary-title, [data-rebuy-title], h2, h3');
    var name = heading && heading.textContent && heading.textContent.trim().replace(/\s+/g, ' ');
    return name ? name.slice(0, 100) : 'Rebuy widget ' + widgetId;
  }

  function rebuyIntentId() {
    var current = getState();
    if (current.intent_id) return current.intent_id;
    if (!rebuyInferredIntentId) rebuyInferredIntentId = uuid();
    return rebuyInferredIntentId;
  }

  function trackRebuyImpression(detail) {
    detail = detail || {};
    var widget = detail.widget || null;
    var provisionalId = rebuyWidgetId(widget, null);
    var root = rebuyRootFor(widget, provisionalId);
    var widgetId = rebuyWidgetId(widget, root);
    if (root && !rebuyRootRendered(root)) return false;
    var items = rebuyItems(detail, widget, root);
    if (!items.length) return false;

    var surface = rebuyPageSurface();
    var signature = [widgetId, surface, items.map(function (item) {
      return item.item_id || item.item_name;
    }).join(',')].join('|');
    if (rebuyImpressions.has(signature)) return false;

    var state = {
      signature: signature,
      intent_id: rebuyIntentId(),
      recommendation_id: uuid(),
      recommendation_version: 'rebuy-widget-' + widgetId,
      recommendation_source: 'rebuy_' + surface,
      item_list_id: 'rebuy_widget_' + widgetId,
      item_list_name: rebuyListName(root, widgetId),
      items: items
    };
    rebuyImpressions.add(signature);
    rebuyWidgetStates.set(widgetId, state);
    markRuntime('data-ca-evidence-rebuy', widgetId);
    markRuntime('data-ca-evidence-rebuy-surface', surface);

    return publish('recommendation_shown', {
      intent_id: state.intent_id,
      recommendation_id: state.recommendation_id,
      recommendation_version: state.recommendation_version,
      recommendation_source: state.recommendation_source,
      item_list_id: state.item_list_id,
      item_list_name: state.item_list_name,
      result_count: items.length,
      items: items
    }, { allowDuplicate: true });
  }

  function rebuySelectionIsDuplicate(widgetId, item) {
    var cutoff = now() - 10000;
    rebuyRecentSelections.forEach(function (timestamp, key) {
      if (timestamp < cutoff) rebuyRecentSelections.delete(key);
    });
    var key = widgetId + '|' + String(item.item_id || item.item_name || 'unknown');
    var previousAt = rebuyRecentSelections.get(key) || 0;
    if (now() - previousAt < 2000) return true;
    rebuyRecentSelections.set(key, now());
    return false;
  }

  function trackRebuySelection(event) {
    var detail = event && event.detail || {};
    trackRebuyImpression(detail);
    var widget = detail.widget || null;
    var provisionalId = rebuyWidgetId(widget, null);
    var root = rebuyRootFor(widget, provisionalId);
    var widgetId = rebuyWidgetId(widget, root);
    var product = detail.product || detail.item || null;
    if (!product) return false;
    var item = rebuyProductItem(product, 0);
    if (!item || !(item.item_id || item.item_name) || rebuySelectionIsDuplicate(widgetId, item)) return false;

    var state = rebuyWidgetStates.get(widgetId);
    if (!state) {
      var surface = rebuyPageSurface();
      state = {
        intent_id: rebuyIntentId(),
        recommendation_id: uuid(),
        recommendation_version: 'rebuy-widget-' + widgetId,
        recommendation_source: 'rebuy_' + surface,
        item_list_id: 'rebuy_widget_' + widgetId,
        item_list_name: rebuyListName(root, widgetId),
        items: [item]
      };
      rebuyWidgetStates.set(widgetId, state);
    }

    var matched = state.items && state.items.find(function (candidate) {
      return candidate.item_id === item.item_id || candidate.item_name === item.item_name;
    });
    if (matched && matched.index != null) item.index = matched.index;
    var variant = product.selected_variant || product.selectedVariant || {};
    var handle = product.handle || product.product_handle || product.productHandle || item.item_id;

    return selectRecommendation({
      intent_id: state.intent_id,
      recommendation_id: state.recommendation_id,
      recommendation_version: state.recommendation_version,
      recommendation_source: state.recommendation_source,
      item_list_id: state.item_list_id,
      item_list_name: state.item_list_name,
      product_handle: handle,
      variant_id: variant.id || product.variant_id || product.variantId || item.variant_id || null,
      position: item.index,
      item: item
    });
  }

  function scanRebuyWidgets() {
    if (!document.querySelectorAll) return;
    Array.prototype.forEach.call(document.querySelectorAll('[data-rebuy-id]'), function (root) {
      if (!rebuyRootRendered(root)) return;
      trackRebuyImpression({
        widget: {
          id: root.getAttribute('data-rebuy-id'),
          el: root
        }
      });
    });
  }

  function scheduleRebuyScan() {
    if (rebuyScanTimer) window.clearTimeout(rebuyScanTimer);
    rebuyScanTimer = window.setTimeout(function () {
      rebuyScanTimer = null;
      scanRebuyWidgets();
    }, 120);
  }

  function containsRebuyNode(node) {
    if (!node || node.nodeType !== 1) return false;
    if (node.matches && node.matches('[data-rebuy-id], .rebuy-widget, .rebuy-product-block')) return true;
    return Boolean(node.querySelector && node.querySelector('[data-rebuy-id], .rebuy-widget, .rebuy-product-block'));
  }

  function setupRebuyTracking() {
    if (!document.addEventListener) return;
    ['rebuy.ready', 'rebuy.productsChange', 'rebuy.refresh', 'rebuy.show'].forEach(function (eventName) {
      document.addEventListener(eventName, function (event) {
        window.setTimeout(function () { trackRebuyImpression(event && event.detail || {}); }, 0);
      });
    });
    document.addEventListener('rebuy.view', trackRebuySelection);
    document.addEventListener('rebuy.add', trackRebuySelection);
    document.addEventListener('rebuy.loaded', scheduleRebuyScan);
    document.addEventListener('rebuy:smartcart.show', scheduleRebuyScan);
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', scheduleRebuyScan, { once: true });
    } else {
      scheduleRebuyScan();
    }
  }

  function guideAction(payload) {
    return publish('guide_action', payload || {});
  }

  function supportAction(payload) {
    return publish('support_action', payload || {});
  }

  function debouncedPublish(key, name, payload) {
    if (debounceTimers.has(key)) window.clearTimeout(debounceTimers.get(key));
    debounceTimers.set(key, window.setTimeout(function () {
      debounceTimers.delete(key);
      publish(name, payload);
    }, FILTER_DEBOUNCE_MS));
  }

  function safeCount(payload) {
    return payload.result_count != null ? payload.result_count :
      payload.product_count != null ? payload.product_count :
      payload.results != null && Array.isArray(payload.results) ? payload.results.length : null;
  }

  function legacyPayload(payload, eventName) {
    var copy = Object.assign({}, payload);
    delete copy.event;
    copy.legacy_event = eventName;
    return copy;
  }

  function safeContext(payload, eventName) {
    var destination = payload.destination || payload.href || null;
    if (typeof destination === 'string') destination = destination.split('?')[0].split('#')[0];
    return {
      legacy_event: eventName,
      event_category: payload.event_category || null,
      feature: payload.feature || null,
      page_type: payload.page_type || null,
      product_handle: payload.product_handle || payload.handle || null,
      target_handle: payload.target_handle || null,
      variant_id: payload.variant_id || null,
      position: payload.position != null ? payload.position : payload.index,
      destination_path: destination,
      result_count: safeCount(payload),
      query_length: payload.query_length != null ? payload.query_length : null,
      comparison_count: payload.comparison_count != null ? payload.comparison_count : payload.compared_products,
      card_count: payload.card_count != null ? payload.card_count : payload.quick_fit_card_count,
      guide_cards: payload.guide_cards != null ? payload.guide_cards : null,
      purchase_source: payload.purchase_source || null,
      items: normalizeItems(payload)
    };
  }

  function push(payload) {
    if (!payload || typeof payload !== 'object') return false;
    var eventName = String(payload.event || 'legacy_interaction').slice(0, 100);
    var details = legacyPayload(payload, eventName);
    var action = String(payload.quiz_action || payload.action || '').toLowerCase();

    if (/^search_(best_match_shown|zero_results_recovery_shown|recovery_click)$/.test(eventName)) {
      return publish(eventName, details);
    }

    if (eventName === 'find_your_match') {
      if (action === 'complete') {
        captureIntent({
          intent_source: 'find_your_match',
          intent_type: 'declared',
          primary_goal: payload.priority || 'unknown',
          experience_level: payload.experience || 'unknown',
          recommendation_version: 'find-your-match-theme-1',
          legacy_event: eventName
        });
        return showRecommendations({
          recommendation_source: 'find_your_match',
          item_list_id: 'find_your_match_results',
          item_list_name: 'Find Your Match results',
          result_count: safeCount(payload),
          legacy_event: eventName
        });
      }
      if (action === 'product_click') return selectRecommendation(details);
      return guideAction(Object.assign(details, { guide_id: 'find_your_match', action: action || 'interaction' }));
    }

    if (/_finder_result$/.test(eventName)) {
      captureIntent({
        intent_source: eventName.replace(/_finder_result$/, ''),
        intent_type: 'declared',
        result_count: safeCount(payload),
        recommendation_version: 'theme-1',
        legacy_event: eventName
      });
      return showRecommendations({
        recommendation_source: eventName.replace(/_finder_result$/, ''),
        item_list_id: eventName + '_results',
        item_list_name: eventName.replace(/_/g, ' '),
        result_count: safeCount(payload),
        items: normalizeItems(payload),
        legacy_event: eventName
      });
    }

    if (/_finder_view$|_comparison_view$|_quick_fit_view$/.test(eventName)) {
      return showRecommendations({
        recommendation_source: eventName.replace(/_(finder|comparison|quick_fit)_view$/, ''),
        item_list_id: eventName,
        item_list_name: eventName.replace(/_/g, ' '),
        result_count: safeCount(payload),
        items: normalizeItems(payload),
        legacy_event: eventName
      });
    }

    if (/_recommendation_select$/.test(eventName) || eventName === 'satisfyer_route_select') {
      var recommendationSource = eventName.replace(/_(recommendation_select|route_select)$/, '');
      captureIntent({
        intent_source: recommendationSource,
        intent_type: 'declared',
        primary_goal: payload.recommendation_intent || payload.route || 'unknown',
        recommendation_version: 'theme-1',
        legacy_event: eventName
      });
      return showRecommendations({
        recommendation_source: recommendationSource,
        item_list_id: eventName + '_results',
        item_list_name: eventName.replace(/_/g, ' '),
        result_count: safeCount(payload),
        items: normalizeItems(payload),
        legacy_event: eventName
      });
    }

    if (/_recommendation_click$|_beginner_pick_click$|_finder_product_click$|_product_click$|_comparison_click$/.test(eventName) || eventName === 'ca_compare_product_clicked') {
      return selectRecommendation(safeContext(payload, eventName));
    }

    if (/filter_(select|applied)$|_finder_filter$/.test(eventName)) {
      return debouncedPublish(eventName, 'filter_applied', safeContext(payload, eventName));
    }
    if (/filter_remove$|filter_removed$/.test(eventName)) {
      return debouncedPublish(eventName, 'filter_removed', safeContext(payload, eventName));
    }
    if (/filter_clear$|finder_clear$/.test(eventName)) {
      return debouncedPublish(eventName, 'filter_cleared', safeContext(payload, eventName));
    }

    if (/_finder_search$/.test(eventName)) {
      return publish('finder_search', safeContext(payload, eventName));
    }

    if (/^help_/.test(eventName)) {
      return publish('help_interaction', Object.assign(safeContext(payload, eventName), {
        action: eventName.replace(/^help_/, '')
      }));
    }

    if (/support_form_submit$/.test(eventName)) {
      return publish('support_request_submitted', safeContext(payload, eventName), { allowDuplicate: true });
    }
    if (/support|faq|contact|replacement|purchase_source/.test(eventName)) {
      return supportAction(Object.assign(safeContext(payload, eventName), { action: eventName }));
    }

    if (eventName === 'search_redirect') return publish('search_redirect', safeContext(payload, eventName));

    if (/^ca_compare_/.test(eventName)) {
      return publish('comparison_action', Object.assign(safeContext(payload, eventName), {
        action: eventName.replace(/^ca_compare_/, '')
      }));
    }

    if (/guide|gallery_view|collection_click|brand_click|accessory_click|route_select/.test(eventName)) {
      return guideAction(Object.assign(safeContext(payload, eventName), { action: eventName }));
    }

    if (/catalogue_(loaded|load_error)$/.test(eventName)) {
      return document.dispatchEvent(new CustomEvent('ca:evidence:diagnostic', {
        detail: sanitize(safeContext(payload, eventName))
      }));
    }

    return publish('legacy_interaction', safeContext(payload, eventName));
  }

  function destinationType(element) {
    var href = element.getAttribute('href') || '';
    if (/\/products\//.test(href)) return 'product';
    if (/\/collections\//.test(href)) return 'collection';
    if (/\/pages\//.test(href)) return 'page';
    if (/\/blogs\/[^/]+\/[^/?#]+/.test(href)) return 'article';
    if (/\/blogs\//.test(href)) return 'blog';
    if (/\/search(?:[/?#]|$)/.test(href)) return 'search';
    if (/\/cart(?:[/?#]|$)/.test(href)) return 'cart';
    if (/\/account(?:[/?#]|$)/.test(href)) return 'account';
    if (href.charAt(0) === '#') return 'anchor';
    if (href === '/' || /^(?:https?:\/\/[^/]+)?\/?(?:[?#].*)?$/.test(href)) return 'home';
    if (/^(?:https?:)?\/\//.test(href)) return 'external';
    return href ? 'other_internal' : 'action';
  }

  function closestElement(element, selector) {
    try {
      return element && element.closest ? element.closest(selector) : null;
    } catch (error) {
      return null;
    }
  }

  function internalDestinationPath(element) {
    if (!element || !element.getAttribute) return null;
    var href = String(element.getAttribute('href') || '');
    if (!href || /^(?:mailto|tel|sms|javascript):/i.test(href)) return null;
    if (href.charAt(0) === '#') return window.location.pathname || '/';
    if (href.charAt(0) === '/') return href.split(/[?#]/)[0] || '/';

    try {
      var base = window.location.href || (window.location.origin || '') + (window.location.pathname || '/');
      var url = new URL(href, base);
      if (window.location.origin && url.origin !== window.location.origin) return null;
      return url.pathname || '/';
    } catch (error) {
      return null;
    }
  }

  function filterGroupId(name) {
    var key = String(name || '').toLowerCase();
    if (key === 'sort_by' || /filter-sort/.test(key)) return 'sort';
    if (/availability|in_stock|stock/.test(key)) return 'availability';
    if (/price|range/.test(key)) return 'price';
    if (/vendor|brand/.test(key)) return 'brand';
    if (/product_type|\.type/.test(key)) return 'product_type';
    if (/category/.test(key)) return 'category';
    if (/shop_by_need|need_paths?/.test(key)) return 'shop_by_need';
    if (/storefront_control|control_method|how_you_control/.test(key)) return 'control';
    if (/water_resistance|waterproof/.test(key)) return 'water_resistance';
    if (/storefront_feel|storefront_sensation|how_it_feels|sensation/.test(key)) return 'sensation';
    if (/wearable_fit|wearable/.test(key)) return 'wearable_fit';
    if (/use_?and_?convenience|use_?convenience|convenience/.test(key)) return 'use_convenience';
    if (/insertable_length/.test(key)) return 'insertable_length';
    if (/power_type|power_source/.test(key)) return 'power_type';
    if (/sound|noise|discretion/.test(key)) return 'sound_discretion';
    if (/material/.test(key)) return 'material';
    if (/colou?r/.test(key)) return 'colour';
    if (/custom\.feature|\.feature$/.test(key)) return 'feature';
    if (/\.tag|tags?/.test(key)) return 'tag';
    if (/\.option\.|variant/.test(key)) return 'variant_option';
    if (/metafield|\.m\./.test(key)) return 'product_attribute';
    return 'other';
  }

  function filterFeatureForGroup(groupId) {
    return groupId === 'all' ? 'filter_all' : 'filter_' + (groupId || 'other');
  }

  function sortId(value) {
    var key = String(value || '').toLowerCase().replace(/-/g, '_');
    var allowed = {
      manual: true,
      best_selling: true,
      title_ascending: true,
      title_descending: true,
      price_ascending: true,
      price_descending: true,
      created_ascending: true,
      created_descending: true,
      relevance: true
    };
    return allowed[key] ? key : 'other';
  }

  function activeFilterCount() {
    if (!document.querySelectorAll) return null;
    var activeChips = document.querySelectorAll('facet-filters a.active-filter');
    if (activeChips && activeChips.length) return activeChips.length;

    var inputs = document.querySelectorAll('facet-filters [name^="filter."]');
    var count = 0;
    Array.prototype.forEach.call(inputs || [], function (input) {
      var type = String(input.type || '').toLowerCase();
      if (type === 'checkbox' || type === 'radio') {
        if (input.checked) count += 1;
      } else if (String(input.value || '').trim()) {
        count += 1;
      }
    });
    return count;
  }

  function facetSnapshot() {
    var resultCount = null;
    var countNode = document.querySelector && document.querySelector(
      'facet-filters .js-close-drawer-mob, [data-result-count], .products-toolbar__title'
    );
    var countText = countNode && countNode.textContent || '';
    var countMatch = String(countText).replace(/,/g, '').match(/\d+/);
    if (countMatch) resultCount = Number(countMatch[0]);

    var cards = [];
    if (document.querySelectorAll) {
      cards = document.querySelectorAll('#filter-results product-card');
      if (!cards.length) cards = document.querySelectorAll('#filter-results .card');
      if (!cards.length) cards = document.querySelectorAll('#filter-results > ul > li');
    }
    // Count rendered products, not child inputs bearing the same product ID.
    // This is not a viewport-impression metric; no layout reads are required.
    var renderedProducts = new Set();
    Array.prototype.forEach.call(cards, function (card) {
      if (card.hidden || card.closest && card.closest('[hidden]')) return;
      var productId = card.getAttribute && card.getAttribute('data-product-id');
      renderedProducts.add(productId || card);
    });
    return {
      result_count: resultCount,
      visible_card_count: renderedProducts.size,
      active_filter_count: activeFilterCount()
    };
  }

  function facetPayload(interaction, after) {
    var before = interaction.before || {};
    var finishedAt = now();
    var payload = {
      filter_group_id: interaction.filter_group_id,
      feature: interaction.feature,
      page_type: rebuyPageSurface(),
      result_count_before: before.result_count,
      result_count_after: after.result_count,
      visible_card_count_before: before.visible_card_count,
      visible_card_count_after: after.visible_card_count,
      active_filter_count_before: before.active_filter_count,
      active_filter_count_after: after.active_filter_count,
      latency_ms: Math.max(0, finishedAt - interaction.started_at),
      filter_ui_version: FILTER_UI_VERSION
    };
    if (interaction.sort_id) payload.sort_id = interaction.sort_id;
    return payload;
  }

  function publishFacetInteraction(interaction, after) {
    return publish(interaction.name, facetPayload(interaction, after));
  }

  function flushFacetInteractions(reason) {
    if (pendingFacetTimer) window.clearTimeout(pendingFacetTimer);
    pendingFacetTimer = null;
    var interactions = pendingFacetInteractions.slice(0, 20);
    pendingFacetInteractions = [];
    if (!interactions.length) return;

    var after = facetSnapshot();
    lastFacetSnapshot = after;
    interactions.forEach(function (interaction) {
      publishFacetInteraction(interaction, after);
    });

    if (reason === 'updated') {
      var measuredGroups = {};
      interactions.forEach(function (interaction) {
        if (measuredGroups[interaction.filter_group_id]) return;
        measuredGroups[interaction.filter_group_id] = true;
        publish('filter_results_updated', facetPayload(interaction, after));
      });
    }
  }

  function queueFacetInteraction(name, filterGroupIdValue, feature, extra) {
    var before = pendingFacetInteractions.length
      ? facetSnapshot()
      : lastFacetSnapshot || facetSnapshot();
    pendingFacetInteractions.push(Object.assign({
      name: name,
      filter_group_id: filterGroupIdValue,
      feature: feature || filterFeatureForGroup(filterGroupIdValue),
      before: before,
      started_at: now()
    }, extra || {}));
    if (pendingFacetInteractions.length > 20) pendingFacetInteractions.shift();
    if (pendingFacetTimer) window.clearTimeout(pendingFacetTimer);
    pendingFacetTimer = window.setTimeout(function () {
      flushFacetInteractions('timeout');
    }, FILTER_UPDATE_TIMEOUT_MS);
  }

  function filterGroupFromElement(element) {
    var named = element && (element.name || element.getAttribute && element.getAttribute('name'));
    if (named) return filterGroupId(named);
    var filterRoot = closestElement(element, '.filter, details-disclosure, details');
    var input = filterRoot && filterRoot.querySelector && filterRoot.querySelector('[name^="filter."]');
    if (input && input.name) return filterGroupId(input.name);
    var rootId = filterRoot && (filterRoot.id || filterRoot.getAttribute && filterRoot.getAttribute('id'));
    return filterGroupId(rootId);
  }

  function filterParameterCounts(url) {
    var counts = {};
    url.searchParams.forEach(function (_value, key) {
      if (String(key).indexOf('filter.') !== 0) return;
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }

  function removedFilterGroupFromActiveLink(anchor) {
    if (!anchor || !anchor.getAttribute) return 'active_filter';
    var href = anchor.getAttribute('href');
    if (!href) return 'active_filter';

    try {
      var base = window.location.href ||
        (window.location.origin || '') + (window.location.pathname || '/');
      var currentCounts = filterParameterCounts(new URL(base));
      var nextCounts = filterParameterCounts(new URL(href, base));
      var removedGroups = {};

      Object.keys(currentCounts).forEach(function (parameterName) {
        if (currentCounts[parameterName] <= (nextCounts[parameterName] || 0)) return;
        removedGroups[filterGroupId(parameterName)] = true;
      });

      var groups = Object.keys(removedGroups);
      return groups.length === 1 ? groups[0] : 'active_filter';
    } catch (error) {
      return 'active_filter';
    }
  }

  function publishFacetUiEvent(name, filterGroupIdValue, feature) {
    var state = facetSnapshot();
    return publish(name, {
      filter_group_id: filterGroupIdValue,
      feature: feature || filterFeatureForGroup(filterGroupIdValue),
      page_type: rebuyPageSurface(),
      result_count_after: state.result_count,
      visible_card_count_after: state.visible_card_count,
      active_filter_count_after: state.active_filter_count,
      filter_ui_version: FILTER_UI_VERSION
    });
  }

  function setupFacetTracking() {
    if (!document.addEventListener) return;
    lastFacetSnapshot = facetSnapshot();

    document.addEventListener('change', function (event) {
      var target = event && event.target;
      if (!target) return;

      var selectedSort = event.detail && event.detail.selectedValue;
      var isCustomSort = target.tagName === 'CUSTOM-SELECT' &&
        Boolean(closestElement(target, '.products-toolbar__sort, facet-filters'));
      var name = target.name || target.getAttribute && target.getAttribute('name') || '';
      if (name === 'sort_by' || isCustomSort) {
        var sortValue = selectedSort || target.value || '';
        var safeSortId = sortId(sortValue);
        if (document.querySelector && document.querySelector('facet-filters')) {
          queueFacetInteraction('sort_applied', 'sort', 'sort_' + safeSortId, { sort_id: safeSortId });
        } else {
          var sortInteraction = {
            name: 'sort_applied',
            filter_group_id: 'sort',
            feature: 'sort_' + safeSortId,
            sort_id: safeSortId,
            before: facetSnapshot(),
            started_at: now()
          };
          publishFacetInteraction(sortInteraction, facetSnapshot());
        }
        return;
      }

      if (String(name).indexOf('filter.') !== 0) return;
      var action = target.checked === false ? 'filter_removed' : 'filter_applied';
      var groupId = filterGroupId(name);
      queueFacetInteraction(action, groupId, filterFeatureForGroup(groupId));
    }, true);

    document.addEventListener('click', function (event) {
      var target = event && event.target;
      if (!target) return;
      var summary = closestElement(target, 'summary');
      var summaryFilter = summary && closestElement(summary, 'details.filter');
      if (summaryFilter && event.isTrusted !== false) {
        pendingFacetGroupToggles.set(summaryFilter, {
          expected_open: !Boolean(summaryFilter.open),
          started_at: now()
        });
      }
      var activeFilter = closestElement(target, 'a.active-filter');
      var clearFilter = closestElement(target, 'a.js-clear-filter');
      var clearAll = closestElement(target, 'a.js-clear-all');
      if (activeFilter) {
        var removedGroup = removedFilterGroupFromActiveLink(activeFilter);
        queueFacetInteraction(
          'filter_removed',
          removedGroup,
          removedGroup === 'active_filter' ? 'filter_active' : filterFeatureForGroup(removedGroup)
        );
      }
      if (clearFilter) {
        var clearGroup = filterGroupFromElement(clearFilter);
        queueFacetInteraction('filter_cleared', clearGroup, filterFeatureForGroup(clearGroup));
      }
      if (clearAll) queueFacetInteraction('filter_cleared', 'all', 'filter_all');
    }, true);

    document.addEventListener('toggle', function (event) {
      var details = event && event.target;
      if (!details || String(details.tagName || '').toUpperCase() !== 'DETAILS') return;
      var filterRoot = closestElement(details, 'details.filter');
      if (!filterRoot) return;
      var userIntent = pendingFacetGroupToggles.get(filterRoot);
      pendingFacetGroupToggles.delete(filterRoot);
      if (!userIntent) return;
      if (now() - userIntent.started_at > FILTER_GROUP_TOGGLE_INTENT_TTL_MS) return;
      if (Boolean(details.open) !== userIntent.expected_open) return;
      var groupId = filterGroupFromElement(filterRoot);
      publishFacetUiEvent(details.open ? 'filter_group_opened' : 'filter_group_closed', groupId);
    }, true);

    document.addEventListener('on:facet-filters:after-open', function () {
      publishFacetUiEvent('filter_drawer_opened', 'all', 'filter_drawer');
    });
    document.addEventListener('on:facet-filters:after-close', function () {
      publishFacetUiEvent('filter_drawer_closed', 'all', 'filter_drawer');
    });
    document.addEventListener('on:facet-filters:updated', function () {
      flushFacetInteractions('updated');
    });
  }

  function navigationFeature(anchor) {
    if (closestElement(anchor, '.breadcrumbs, .breadcrumb, [aria-label*="breadcrumb" i]')) return 'navigation_breadcrumb';
    if (closestElement(anchor, '.drawer, .mobile-menu, .mobile-navigation, [data-mobile-menu], nav-drawer')) {
      return 'navigation_mobile_menu';
    }
    if (closestElement(anchor, 'footer, [role="contentinfo"]')) return 'navigation_footer';
    if (closestElement(anchor, 'header, [role="banner"], .main-nav, .header')) return 'navigation_header';
    if (closestElement(anchor, 'nav')) return 'navigation_other';
    return null;
  }

  function setupNavigationTracking() {
    if (!document.addEventListener) return;
    document.addEventListener('click', function (event) {
      var anchor = closestElement(event && event.target, 'a[href]');
      if (!anchor || closestElement(anchor, '[data-guide-event], facet-filters')) return;
      var feature = navigationFeature(anchor);
      var destinationPath = internalDestinationPath(anchor);
      if (!feature || !destinationPath) return;
      guideAction({
        guide_id: 'site_navigation',
        action: 'navigation_click',
        feature: feature,
        page_type: rebuyPageSurface(),
        destination_type: destinationType(anchor),
        destination_path: destinationPath
      });
    }, true);
  }

  function currentGuideId() {
    var path = classifiedPagePath();
    var match = path.match(/^\/pages\/([^/?#]+)/);
    var slug = match ? match[1] : 'guide';
    return slug.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 80) || 'guide';
  }

  function referrerType() {
    var referrer = String(document.referrer || '');
    if (!referrer) return 'none';
    try {
      var referrerUrl = new URL(referrer);
      if (window.location.origin && referrerUrl.origin === window.location.origin) return 'internal';
      if (/google\.|bing\.|yahoo\.|duckduckgo\.|baidu\./i.test(referrerUrl.hostname)) return 'search';
      if (/facebook\.|instagram\.|tiktok\.|youtube\.|pinterest\.|x\.com$|twitter\./i.test(referrerUrl.hostname)) return 'social';
      return 'referral';
    } catch (error) {
      return 'unknown';
    }
  }

  function isPageNotFound() {
    if (document.querySelector && document.querySelector('[data-ca-page-not-found]')) return true;
    if (document.body && /(?:^|\s)template-404(?:\s|$)/.test(document.body.className || '')) return true;
    return /^404\b/i.test(String(document.title || ''));
  }

  function setupPageNotFoundTracking() {
    if (!isPageNotFound()) return;
    publish('page_not_found', {
      page_type: '404',
      referrer_type: referrerType()
    });
  }

  function webVitalSampled() {
    try {
      var stored = window.sessionStorage.getItem(WEB_VITAL_SAMPLE_KEY);
      if (stored === '1') return true;
      if (stored === '0') return false;
      var sampled = Math.random() < WEB_VITAL_SAMPLE_RATE;
      window.sessionStorage.setItem(WEB_VITAL_SAMPLE_KEY, sampled ? '1' : '0');
      return sampled;
    } catch (error) {
      return Math.random() < WEB_VITAL_SAMPLE_RATE;
    }
  }

  function webVitalRating(name, value) {
    var limits = {
      LCP: [2500, 4000],
      CLS: [0.1, 0.25],
      INP: [200, 500]
    };
    var limit = limits[name];
    if (!limit) return 'unknown';
    if (value <= limit[0]) return 'good';
    if (value <= limit[1]) return 'needs_improvement';
    return 'poor';
  }

  function setupWebVitalTracking() {
    if (!window.PerformanceObserver || !webVitalSampled()) return;

    var values = { LCP: null, CLS: 0, INP: null };
    var published = {};

    function publishVital(name) {
      var value = values[name];
      if (published[name] || value == null) return;
      published[name] = true;
      publish('web_vital', {
        metric_name: name,
        metric_value: Math.round(value * 1000) / 1000,
        metric_rating: webVitalRating(name, value),
        sample_rate: WEB_VITAL_SAMPLE_RATE,
        page_type: isPageNotFound() ? '404' : rebuyPageSurface()
      });
    }

    try {
      new PerformanceObserver(function (entryList) {
        var entries = entryList.getEntries();
        if (entries.length) values.LCP = entries[entries.length - 1].startTime;
      }).observe({ type: 'largest-contentful-paint', buffered: true });
    } catch (error) { /* unsupported observer */ }

    try {
      new PerformanceObserver(function (entryList) {
        entryList.getEntries().forEach(function (entry) {
          if (!entry.hadRecentInput) values.CLS += entry.value;
        });
      }).observe({ type: 'layout-shift', buffered: true });
    } catch (error) { /* unsupported observer */ }

    try {
      new PerformanceObserver(function (entryList) {
        entryList.getEntries().forEach(function (entry) {
          if (entry.interactionId && (values.INP == null || entry.duration > values.INP)) {
            values.INP = entry.duration;
          }
        });
      }).observe({ type: 'event', buffered: true, durationThreshold: 40 });
    } catch (error) { /* unsupported observer */ }

    function finalizeVitals() {
      publishVital('LCP');
      publishVital('CLS');
      publishVital('INP');
    }

    window.setTimeout(finalizeVitals, 10000);
    window.addEventListener('pagehide', finalizeVitals, { once: true });
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') finalizeVitals();
    });
  }

  function publishGuideProgress(percent) {
    if (guideProgressMarks.has(percent)) return;
    guideProgressMarks.add(percent);
    guideAction({
      guide_id: currentGuideId(),
      action: 'progress_' + percent,
      feature: 'guide_progress',
      page_type: 'guide'
    });
  }

  function checkGuideProgress() {
    var root = document.documentElement || {};
    var body = document.body || {};
    var scrollTop = window.pageYOffset != null ? window.pageYOffset : window.scrollY || root.scrollTop || body.scrollTop || 0;
    var viewport = window.innerHeight || root.clientHeight || 0;
    var height = Math.max(root.scrollHeight || 0, body.scrollHeight || 0, viewport);
    var percent = height > viewport ? Math.round((scrollTop + viewport) / height * 100) : 100;
    [25, 50, 75, 100].forEach(function (mark) {
      if (percent >= mark) publishGuideProgress(mark);
    });
  }

  function setupGuideVisibility() {
    if (!window.IntersectionObserver || !document.querySelectorAll) return;
    var seenSections = new WeakSet();
    var sectionObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting || seenSections.has(entry.target)) return;
        seenSections.add(entry.target);
        sectionObserver.unobserve(entry.target);
        guideAction({
          guide_id: currentGuideId(),
          action: 'section_view_' + entry.target.__caGuideSectionIndex,
          feature: 'guide_section',
          page_type: 'guide'
        });
      });
    }, { threshold: 0.5 });

    var main = document.querySelector && document.querySelector('main');
    var sections = main && main.querySelectorAll ? main.querySelectorAll('section, [data-section-id]') : [];
    Array.prototype.slice.call(sections, 0, 20).forEach(function (section, index) {
      section.__caGuideSectionIndex = index + 1;
      sectionObserver.observe(section);
    });

    var seenCtas = new WeakSet();
    guideCtaObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting || seenCtas.has(entry.target)) return;
        seenCtas.add(entry.target);
        guideCtaObserver.unobserve(entry.target);
        guideAction({
          guide_id: currentGuideId(),
          action: 'cta_view',
          feature: 'guide_cta',
          page_type: 'guide',
          destination_type: destinationType(entry.target),
          destination_path: internalDestinationPath(entry.target)
        });
      });
    }, { threshold: 0.5 });
  }

  function setupGuideEngagement() {
    if (rebuyPageSurface() !== 'guide') return;
    guideAction({
      guide_id: currentGuideId(),
      action: 'guide_view',
      feature: 'guide_engagement',
      page_type: 'guide'
    });
    setupGuideVisibility();
    if (window.addEventListener) {
      var progressTimer = null;
      window.addEventListener('scroll', function () {
        if (progressTimer) return;
        progressTimer = window.setTimeout(function () {
          progressTimer = null;
          checkGuideProgress();
        }, 150);
      }, { passive: true });
      window.setTimeout(checkGuideProgress, 0);
    }
  }

  var boundGuideElements = new WeakSet();

  function publishGuideElement(element) {
    var legacyAction = element.getAttribute('data-guide-event') || 'guide_action';
    guideAction({
      guide_id: rebuyPageSurface() === 'guide' ? currentGuideId() : legacyAction.split('_').slice(0, 3).join('_'),
      action: legacyAction,
      legacy_action: legacyAction,
      feature: 'guide_cta',
      page_type: rebuyPageSurface(),
      destination_type: destinationType(element),
      destination_path: internalDestinationPath(element)
    });
  }

  function bindGuideActions(root) {
    var elements = [];
    if (root && root.matches && root.matches('[data-guide-event]')) elements.push(root);
    if (root && root.querySelectorAll) {
      elements = elements.concat(Array.prototype.slice.call(root.querySelectorAll('[data-guide-event]')));
    }
    elements.forEach(function (element) {
      if (boundGuideElements.has(element)) return;
      boundGuideElements.add(element);
      if (guideCtaObserver && rebuyPageSurface() === 'guide') guideCtaObserver.observe(element);
      element.addEventListener('click', function () {
        publishGuideElement(element);
      }, true);
    });
  }

  setupRebuyTracking();
  setupFacetTracking();
  setupNavigationTracking();
  setupGuideEngagement();
  setupPageNotFoundTracking();
  setupWebVitalTracking();
  bindGuideActions(document);
  if (window.MutationObserver) {
    new MutationObserver(function (mutations) {
      var rebuyChanged = false;
      mutations.forEach(function (mutation) {
        Array.prototype.forEach.call(mutation.addedNodes || [], function (node) {
          bindGuideActions(node);
          if (containsRebuyNode(node)) rebuyChanged = true;
        });
      });
      if (rebuyChanged) scheduleRebuyScan();
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  var productHandle = currentProductHandle();
  var productLineage = getState();
  if (productHandle && productLineage.selected_item_id === productHandle) {
    persistCartLineage(productLineage);
  }

  window.CAEvidence = {
    schemaVersion: SCHEMA_VERSION,
    publish: publish,
    push: push,
    captureIntent: captureIntent,
    showRecommendations: showRecommendations,
    selectRecommendation: selectRecommendation,
    guideAction: guideAction,
    supportAction: supportAction,
    clearLineage: function () {
      try { window.localStorage.removeItem(STORAGE_KEY); } catch (error) { /* storage unavailable */ }
      try { window.sessionStorage.removeItem(STORAGE_KEY); } catch (error) { /* storage unavailable */ }
    }
  };

  document.documentElement.setAttribute('data-ca-evidence', SCHEMA_VERSION);
  document.documentElement.setAttribute('data-ca-experience-id', EXPERIENCE_ID);
  document.documentElement.setAttribute('data-ca-experience-variant', EXPERIENCE_VARIANT);
  document.documentElement.setAttribute('data-ca-evidence-interactions', 'filters-navigation-guides');
  pending.forEach(push);
}());
/* CA EVIDENCE END */
